'use client';

import { useState, useEffect } from 'react';

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

export function useAuth() {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    token: null,
    isLoading: true,
    isAuthenticated: false
  });

  useEffect(() => {
    // 从localStorage加载认证状态
    const loadAuthState = () => {
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');

      if (token && userStr) {
        try {
          const user = JSON.parse(userStr);
          setAuthState({
            user,
            token,
            isLoading: false,
            isAuthenticated: true
          });
        } catch (error) {
          // 解析错误，清除认证状态
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setAuthState({
            user: null,
            token: null,
            isLoading: false,
            isAuthenticated: false
          });
        }
      } else {
        setAuthState({
          user: null,
          token: null,
          isLoading: false,
          isAuthenticated: false
        });
      }
    };

    loadAuthState();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (data.success) {
        // 存储认证信息到localStorage
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));

        // 同时存储到cookie，以便中间件访问
        if (typeof document !== 'undefined') {
          document.cookie = `token=${data.token}; path=/; max-age=86400; SameSite=Strict`;
          console.log('Cookie set:', document.cookie);
        }

        setAuthState({
          user: data.user,
          token: data.token,
          isLoading: false,
          isAuthenticated: true
        });

        return { success: true, user: data.user };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('登录错误:', error);
      return { success: false, error: '服务器错误' };
    }
  };

  const register = async (name: string, email: string, password: string) => {
    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name, email, password })
      });

      const data = await response.json();

      if (data.success) {
        return { success: true, user: data.user };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('注册错误:', error);
      return { success: false, error: '服务器错误' };
    }
  };

  const logout = () => {
    // 清除localStorage中的认证信息
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    // 同时清除cookie
    if (typeof document !== 'undefined') {
      document.cookie = 'token=; path=/; max-age=0; HttpOnly; SameSite=Strict';
    }

    setAuthState({
      user: null,
      token: null,
      isLoading: false,
      isAuthenticated: false
    });
  };

  return {
    ...authState,
    login,
    register,
    logout
  };
}

// 检查用户是否已登录
export function checkAuth() {
  // 只在客户端运行时访问localStorage
  if (typeof window !== 'undefined') {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');

    if (token && userStr) {
      try {
        const user = JSON.parse(userStr);
        return { isAuthenticated: true, user };
      } catch (error) {
        console.error('Check auth error:', error);
        return { isAuthenticated: false, user: null };
      }
    } else {
      console.log('No token or user in localStorage');
    }
  } else {
    console.log('Window is undefined, server-side rendering');
  }

  return { isAuthenticated: false, user: null };
}

// 检查用户是否为管理员
export function isAdmin() {
  // 只在客户端运行时访问localStorage
  if (typeof window !== 'undefined') {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        return user.role.toLowerCase() === 'admin';
      } catch (error) {
        return false;
      }
    }
  }
  return false;
}